import Navbar from './Navbar'
import Footer from './Footer'
import SEO from './SEO'
import RegMark from './ui/RegMark'
import { IconArrowRight } from './ui/Icon'
import useHashScrollOnLoad from '../hooks/useHashScrollOnLoad'
import './corporate.css'

/**
 * Shared shell + section markup for the Corporate / About page, rendered by the
 * three thin locale wrappers (Kurumsal / Corporate / Unternehmen). Keeps the TR/
 * EN/DE pages in lockstep: same structure and ARIA — only the copy (`data`),
 * localized routes (`langLinks`, `contactHref`), and anchor ids differ per locale.
 *
 * Layout: each section is a "rail" — the h2 holds the left four columns and
 * the content reads in an editorial column to its right.
 *
 * Anchor ids are passed in explicitly (not taken from `data.*.id`) so they match
 * the EXISTING navbar corporate-dropdown hrefs exactly.
 *
 * @param {Object} props
 * @param {'tr'|'en'|'de'} props.lang
 * @param {import('../data/corporate-i18n').CorporateLocale} props.data
 * @param {{ tr: string, en: string, de: string }} props.langLinks
 * @param {string} props.canonicalUrl   the page's path (e.g. '/kurumsal'): og:url; the canonical link itself is CanonicalManager's
 * @param {string} props.contactHref    localized contact anchor (e.g. '/#contact')
 * @param {string} props.skipLabel      localized "skip to content" label
 * @param {{ about: string, vision: string, values: string, quality: string, certifications: string }} props.anchors
 * @param {Object} [props.jsonLd]       optional structured-data block
 */
export default function CorporatePage({
    lang,
    data,
    langLinks,
    canonicalUrl,
    contactHref,
    skipLabel,
    anchors,
    jsonLd
}) {
    useHashScrollOnLoad()
    const { hero, about, vision, values, quality, certifications, cta, seo } = data

    return (
        <>
            <SEO
                title={seo.title}
                description={seo.description}
                url={canonicalUrl}
                lang={lang}
                jsonLd={jsonLd}
            />

            <a className="skip-link" href="#main-content">{skipLabel}</a>

            <Navbar lang={lang} langLinks={langLinks} />

            <main id="main-content" className="corp" lang={lang}>
                {/* Page header: typographic, on the sweep */}
                <section className="corp-head container" aria-labelledby="corporate-hero-heading">
                    <h1 id="corporate-hero-heading">{hero.title}</h1>
                    <p className="lead">{hero.lead}</p>
                </section>

                {/* About */}
                <section className="corp-section" id={anchors.about} aria-labelledby="corporate-about-heading">
                    <div className="container corp-rail">
                        <h2 id="corporate-about-heading">{about.heading}</h2>
                        <div className="corp-prose">
                            {about.paragraphs.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Vision & Mission: two text columns on one hairline */}
                <section className="corp-section" id={anchors.vision} aria-labelledby="corporate-vision-heading">
                    <div className="container corp-rail">
                        <h2 id="corporate-vision-heading">{vision.heading}</h2>
                        <div className="corp-duo">
                            {[vision.vision, vision.mission].map((item) => (
                                <article className="corp-duo-item" key={item.h}>
                                    <h3>{item.h}</h3>
                                    <p>{item.body}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Values: rows read like a ruler (numbering is CSS-generated) */}
                <section className="corp-section" id={anchors.values} aria-labelledby="corporate-values-heading">
                    <div className="container corp-rail">
                        <h2 id="corporate-values-heading">{values.heading}</h2>
                        <ol className="corp-values" data-reveal="stagger">
                            {values.items.map((item, index) => (
                                <li className="corp-value" key={item.title} style={{ '--i': index }}>
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* Quality: prose, then the pull quote at display scale */}
                <section className="corp-section" id={anchors.quality} aria-labelledby="corporate-quality-heading">
                    <div className="container">
                        <div className="corp-rail">
                            <h2 id="corporate-quality-heading">{quality.heading}</h2>
                            <div className="corp-prose">
                                {quality.paragraphs.map((paragraph) => (
                                    <p key={paragraph}>{paragraph}</p>
                                ))}
                            </div>
                        </div>
                        <figure className="corp-quote plate--marks" data-reveal="rise">
                            <RegMark className="reg corp-quote-mark" size={22} />
                            <blockquote>
                                <p>{quality.pullQuote}</p>
                            </blockquote>
                        </figure>
                    </div>
                </section>

                {/* Certificates: a title block, exactly as the copy states */}
                <section
                    className="corp-section"
                    id={anchors.certifications}
                    aria-labelledby="corporate-certifications-heading"
                >
                    <div className="container corp-rail">
                        <h2 id="corporate-certifications-heading">{certifications.heading}</h2>
                        <div className="corp-cert">
                            <table className="spec">
                                <tbody>
                                    <tr>
                                        <th scope="row">{certifications.standard}</th>
                                        <td>{certifications.body}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <a href={contactHref} className="link-arrow">
                                {certifications.ctaLabel}
                                <IconArrowRight />
                            </a>
                        </div>
                    </div>
                </section>

                {/* Final CTA: a full-bleed polymer field */}
                <section className="corp-cta polymer" aria-labelledby="corporate-cta-heading">
                    <div className="container corp-cta-grid">
                        <h2 id="corporate-cta-heading">{cta.heading}</h2>
                        <div className="corp-cta-body">
                            <p>{cta.body}</p>
                            <a href={contactHref} className="btn">
                                {cta.buttonLabel}
                                <IconArrowRight />
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            <Footer lang={lang} />
        </>
    )
}
